import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import 'animate.css';

const NAV = '#061C5B';

const Recursos = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.25 });
  const [btnHovered, setBtnHovered] = useState(false);

  return (
    <>
      <style>{`
        @keyframes floatOrb1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%       { transform: translate(30px, -20px) scale(1.08); }
        }
        @keyframes floatOrb2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%       { transform: translate(-25px, 15px) scale(1.06); }
        }
        @keyframes shimmerBtn {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes pulseBadgeDot {
          0%, 100% { box-shadow: 0 0 0 0 rgba(147,197,253,0.6); opacity: 1; }
          50%       { box-shadow: 0 0 0 6px rgba(147,197,253,0); opacity: 0.7; }
        }
        @keyframes glowLine {
          0%, 100% { opacity: 0.25; }
          50%       { opacity: 0.6; }
        }
        .recursos-btn-pulse::after {
          content: '';
          position: absolute;
          inset: -4px;
          border-radius: 50px;
          background: rgba(255,255,255,0.2);
          opacity: 0;
          animation: pulseRing 2.5s ease-out infinite;
        }
        @keyframes pulseRing {
          0%   { transform: scale(0.95); opacity: 0.6; }
          80%  { transform: scale(1.1);  opacity: 0; }
          100% { transform: scale(1.1);  opacity: 0; }
        }
        @media (max-width: 640px) {
          .recursos-titulo { font-size: 1.8rem !important; }
        }
      `}</style>

      <section
        ref={ref}
        id="recursos"
        style={{
          position: 'relative',
          padding: '110px 24px',
          overflow: 'hidden',
          background: `linear-gradient(145deg, ${NAV} 0%, #0A2B7C 50%, #0e3ba0 100%)`,
        }}
      >
        {/* ── Patrón SVG de malla punteada ─────────────────── */}
        <svg
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            opacity: 0.18, pointerEvents: 'none',
          }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern id="dots" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
              <circle cx="1.5" cy="1.5" r="1.5" fill="rgba(255,255,255,0.65)" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)" />
        </svg>

        {/* ── Líneas diagonales SVG ─────────────────────────── */}
        <svg
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            opacity: 0.06, pointerEvents: 'none',
          }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern id="diag" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="40" stroke="white" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#diag)" />
        </svg>

        {/* ── Resplandor central ───────────────────────────── */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '700px', height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(ellipse, rgba(99,102,241,0.14) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* ── Orbs animados ─────────────────────────────────── */}
        <div style={{
          position: 'absolute', top: '-120px', right: '-80px',
          width: '560px', height: '560px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.24) 0%, transparent 65%)',
          animation: 'floatOrb1 9s ease-in-out infinite',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-100px', left: '-60px',
          width: '480px', height: '480px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.22) 0%, transparent 65%)',
          animation: 'floatOrb2 11s ease-in-out infinite',
          pointerEvents: 'none',
        }} />
        {/* Orb extra esquina inferior derecha */}
        <div style={{
          position: 'absolute', bottom: '-40px', right: '-40px',
          width: '280px', height: '280px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(16,185,129,0.16) 0%, transparent 70%)',
          animation: 'floatOrb1 13s ease-in-out infinite reverse',
          pointerEvents: 'none',
        }} />

        {/* Línea superior animada */}
        <div style={{
          position: 'absolute', top: 0, left: '20%', right: '20%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
          animation: 'glowLine 4s ease-in-out infinite',
        }} />

        {/* ── Contenido ─────────────────────────────────────── */}
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Badge con dot pulsante */}
          <div
            className={inView ? 'animate__animated animate__fadeInDown' : 'opacity-0'}
            style={{ animationDuration: '0.6s', marginBottom: '28px' }}
          >
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.18)',
              padding: '7px 20px 7px 12px',
              borderRadius: '50px',
            }}>
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: '#93c5fd',
                flexShrink: 0,
                animation: 'pulseBadgeDot 2s ease-in-out infinite',
              }} />
              <p style={{
                fontFamily: 'Montserrat',
                fontWeight: 700,
                fontSize: '0.72rem',
                color: '#93c5fd',
                margin: 0,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                WebkitTextFillColor: '#93c5fd',
                WebkitTextStroke: 0,
              }}>
                Biblioteca Digital Gratuita
              </p>
            </div>
          </div>

          {/* Título */}
          <h2
            className={`recursos-titulo ${inView ? 'animate__animated animate__fadeInUp' : 'opacity-0'}`}
            style={{
              animationDuration: '0.65s',
              animationDelay: '0.15s',
              fontFamily: 'Escobar',
              fontSize: 'clamp(2rem, 3.8vw, 3rem)',
              color: '#ffffff',
              WebkitTextFillColor: '#fff',
              WebkitTextStroke: 0,
              margin: '0 0 20px',
              lineHeight: 1.25,
            }}
          >
            DS cyber tiene recursos especializados para ti
          </h2>

          {/* Línea divisoria decorativa */}
          <div
            className={inView ? 'animate__animated animate__fadeIn' : 'opacity-0'}
            style={{
              animationDelay: '0.28s',
              animationDuration: '0.5s',
              display: 'flex',
              justifyContent: 'center',
              gap: '6px',
              marginBottom: '24px',
            }}
          >
            <div style={{ height: '2px', width: '36px', background: '#3b82f6', borderRadius: '2px' }} />
            <div style={{ height: '2px', width: '12px', background: '#93c5fd', borderRadius: '2px' }} />
            <div style={{ height: '2px', width: '6px', background: 'rgba(255,255,255,0.3)', borderRadius: '2px' }} />
          </div>

          {/* Descripción */}
          <p
            className={inView ? 'animate__animated animate__fadeInUp' : 'opacity-0'}
            style={{
              animationDelay: '0.3s',
              animationDuration: '0.65s',
              fontFamily: 'Montserrat',
              fontSize: '1.05rem',
              color: 'rgba(255,255,255,0.72)',
              lineHeight: 1.85,
              margin: '0 0 48px',
            }}
          >
            Hemos recopilado una colección de videos, documentos PDF y cursos
            gratuitos sobre ciberseguridad, inteligencia artificial y
            consultoría tecnológica para que puedas aprender a tu propio ritmo.
          </p>

          {/* Botón CTA con efecto de brillo y pulso */}
          <div
            className={inView ? 'animate__animated animate__zoomIn' : 'opacity-0'}
            style={{
              animationDelay: '0.48s',
              animationDuration: '0.5s',
              display: 'inline-block',
              position: 'relative',
            }}
          >
            {/* Anillo de pulso continuo */}
            <div className="recursos-btn-pulse" style={{ position: 'absolute', inset: 0, borderRadius: '50px' }} />

            <Link
              to="/recursos"
              onMouseEnter={() => setBtnHovered(true)}
              onMouseLeave={() => setBtnHovered(false)}
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                /* Shimmer animado en hover */
                background: btnHovered
                  ? 'linear-gradient(90deg, #fff 0%, #e0f2fe 40%, #fff 80%, #e0f2fe 100%)'
                  : '#ffffff',
                backgroundSize: '200% 100%',
                animation: btnHovered ? 'shimmerBtn 1.4s linear infinite' : 'none',
                color: NAV,
                fontFamily: 'Montserrat',
                fontWeight: 800,
                fontSize: '1rem',
                padding: '17px 44px',
                borderRadius: '50px',
                textDecoration: 'none',
                boxShadow: btnHovered
                  ? `0 0 0 6px rgba(255,255,255,0.12), 0 20px 48px rgba(0,0,0,0.35)`
                  : '0 8px 28px rgba(0,0,0,0.25)',
                transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                transform: btnHovered ? 'translateY(-4px) scale(1.04)' : 'translateY(0) scale(1)',
                letterSpacing: '0.4px',
                WebkitTextFillColor: NAV,
                WebkitTextStroke: 0,
              }}
            >
              Ver Recursos
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20" height="20"
                fill="none" viewBox="0 0 24 24"
                stroke={NAV} strokeWidth={2.5}
                style={{
                  transition: 'transform 0.3s ease',
                  transform: btnHovered ? 'translateX(5px)' : 'translateX(0)',
                }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>


        </div>
      </section>
    </>
  );
};

export default Recursos;
